import type { Endpoint } from 'cadenza/utils/post-message/post-message-api';
import {
  doRequest,
  MessageTypes,
  PostMessageApi,
  windowEndpoint
} from 'cadenza/utils/post-message/post-message-api';

/**
 * Post-Message API for the 'server' window to communicate with the client (often an iframe).
 *
 * Until connected to an client it collects all requests made and starts handling it when after a
 * successful establishment of a connection.
 */
export class ServerPostMessageApi extends PostMessageApi {

  #parentInitParams: Record<string, unknown>;

  #clientEndpoint?: Endpoint;

  /**
   * Initializes a parent post message api.
   *
   * @param options - Options passed to the constructor
   * @param options.parentInitParams - Initial parameters to send to the iframe when making the connection
   */
  constructor ({ parentInitParams }: { parentInitParams?: Record<string, unknown> } = {}) {
    super();
    this.#parentInitParams = parentInitParams ?? {};
    // We are setting up the endpoint here this early, so even when iframe did not yet connect to
    // this channel via the port that we send to it through iframe.contentWindow.postMessage() the
    // messages sent with this api are queued and don't get lost.
    this._switchToNewMessageChannelIfNecessary();
  }

  _switchToNewMessageChannelIfNecessary () {
    if (!this.#clientEndpoint) {
      const messageChannel = new MessageChannel();
      this.endpoint = messageChannel.port1;
      this.#clientEndpoint = messageChannel.port2;
    }
  }

  /**
   * Returns the client endpoint for use in non-iframe views
   */
  get clientEndpoint () {
    return this.#clientEndpoint;
  }

  /**
   * Connects this api to a client window, such as an iframe window for example.
   *
   * @param clientWindow - A client window to connect to.
   * @return A promise reflecting the receiving of the request on iframe content
   * side. First argument states the cancellation of the request by the recipient via
   * event#preventDefault().
   */
  connect (clientWindow: Window) {
    this._switchToNewMessageChannelIfNecessary();
    const clientEndpoint = this.#clientEndpoint;
    this.#clientEndpoint = undefined;

    // If the client is in the same window we can already use our channel endpoint to send the INIT event
    // (as the client already got the client endpoint of the channel as a constructor argument)
    const endpoint = clientWindow ? windowEndpoint(clientWindow) : this.endpoint!;
    // We do not send the endpoint if the client is in the same window
    const transferables = clientWindow ? [ clientEndpoint as MessagePort ] : [];
    return doRequest(
      endpoint,
      {
        type: MessageTypes.INIT,
        detail: {
          endpoint: clientWindow && clientEndpoint,
          parentInitParams: this.#parentInitParams,
          selection: []
        }
      },
      transferables
    ); // Port have to be sent as transferable as it is not a simple object.
  }

  /**
   * Requests the confirmation to change the client's content.
   *
   * @param {object} detail - {*} Additional data sent with the message.
   * @return {Promise.<boolean|NoEndpointDefinedError>} A promise reflecting the receiving of the
   *   request on client content side. First argument states the cancellation of the request by the
   *   recipient via event#preventDefault().
   */
  reload (detail: Transferable[]) {
    return this.request({ type: MessageTypes.RELOAD, detail, cancelable: true });
  }

  get parentInitParams () {
    return this.#parentInitParams;
  }

}
